import { AdMob, BannerAdSize, BannerAdPosition, BannerAdPluginEvents, InterstitialAdPluginEvents, RewardAdPluginEvents } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';
import { isDebugMode } from '../utils/debugUtils';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

/**
 * AdMob Service for managing advertisements in the app
 */
class AdMobService {
    constructor() {
        this.initialized = false;
        this.platform = Capacitor.getPlatform();
        // Check if ads are disabled via environment variable
        this.adsDisabled = process.env.REACT_APP_DISABLE_ADS === 'true';
        // Track if interstitial ad is ready to show
        this.interstitialReady = false;
        this.interstitialLoadListener = null;
        this.interstitialFailListener = null;
        // Web interstitial callbacks
        this.webInterstitialCallbacks = {
            onShow: null,
            onDismiss: null
        };
    }

    /**
     * Check if ads are disabled
     */
    isAdsDisabled() {
        return this.adsDisabled;
    }

    /**
     * Get the appropriate Ad Unit ID based on platform
     */
    getAdUnitId(type) {
        // For web platform, return AdSense IDs
        if (this.platform === 'web') {
            switch (type) {
                case 'interstitial':
                    return {
                        clientId: process.env.REACT_APP_ADSENSE_CLIENT_ID,
                        slotId: process.env.REACT_APP_ADSENSE_SLOT_ID
                    };
                default:
                    return null;
            }
        }

        // For native platforms, return AdMob IDs
        const isAndroid = this.platform === 'android';

        switch (type) {
            case 'banner':
                return isAndroid
                    ? process.env.REACT_APP_ADMOB_BANNER_ID_ANDROID
                    : process.env.REACT_APP_ADMOB_BANNER_ID_IOS;
            case 'interstitial':
                return isAndroid
                    ? process.env.REACT_APP_ADMOB_INTERSTITIAL_ID_ANDROID
                    : process.env.REACT_APP_ADMOB_INTERSTITIAL_ID_IOS;
            case 'rewarded':
                return isAndroid
                    ? process.env.REACT_APP_ADMOB_REWARDED_ID_ANDROID
                    : process.env.REACT_APP_ADMOB_REWARDED_ID_IOS;
            default:
                return null;
        }
    }

    /**
     * Initialize AdMob
     */
    async initialize() {
        if (this.initialized) {
            debugLog('AdMob already initialized');
            return;
        }

        // Only initialize on native platforms
        if (this.platform === 'web') {
            debugLog('AdMob not available on web platform');
            return;
        }

        try {
            await AdMob.initialize({
                initializeForTesting: isDebugMode(),
            });

            this.initialized = true;
            debugLog('AdMob initialized successfully');
        } catch (error) {
            console.error('Failed to initialize AdMob:', error);
        }
    }

    /**
     * Show a banner ad
     */
    async showBanner(position = BannerAdPosition.BOTTOM_CENTER) {
        if (!this.initialized) {
            await this.initialize();
        }

        if (this.platform === 'web') {
            debugLog('Banner ads not available on web');
            return;
        }

        try {
            const adId = this.getAdUnitId('banner');

            await AdMob.showBanner({
                adId,
                adSize: BannerAdSize.ADAPTIVE_BANNER,
                position,
                margin: 0,
            });

            debugLog('Banner ad shown');
        } catch (error) {
            console.error('Failed to show banner ad:', error);
        }
    }

    /**
     * Hide the banner ad
     */
    async hideBanner() {
        if (this.platform === 'web') return;

        try {
            await AdMob.hideBanner();
            debugLog('Banner ad hidden');
        } catch (error) {
            console.error('Failed to hide banner ad:', error);
        }
    }

    /**
     * Resume the banner ad
     */
    async resumeBanner() {
        if (this.platform === 'web') return;

        try {
            await AdMob.resumeBanner();
            debugLog('Banner ad resumed');
        } catch (error) {
            console.error('Failed to resume banner ad:', error);
        }
    }

    /**
     * Remove the banner ad
     */
    async removeBanner() {
        if (this.platform === 'web') return;

        try {
            await AdMob.removeBanner();
            debugLog('Banner ad removed');
        } catch (error) {
            console.error('Failed to remove banner ad:', error);
        }
    }

    /**
     * Check if interstitial ad is ready to show
     */
    isInterstitialReady() {
        return this.interstitialReady;
    }

    /**
     * Prepare an interstitial ad
     * Returns a promise that resolves when the ad is loaded or rejects on failure
     */
    async prepareInterstitial() {
        // Skip if ads are disabled
        if (this.adsDisabled) {
            debugLog('Ads disabled via REACT_APP_DISABLE_ADS');
            return Promise.resolve(false);
        }

        if (!this.initialized) {
            await this.initialize();
        }

        // For web platform, check if AdSense is configured
        if (this.platform === 'web') {
            const adConfig = this.getAdUnitId('interstitial');
            if (adConfig && adConfig.clientId && adConfig.slotId) {
                debugLog('Web interstitial (AdSense) ready');
                this.interstitialReady = true;
                return Promise.resolve(true);
            } else {
                debugLog('AdSense not configured for web interstitial');
                return Promise.resolve(false);
            }
        }

        // Reset ready state
        this.interstitialReady = false;

        // Remove previous listeners if any
        if (this.interstitialLoadListener) {
            this.interstitialLoadListener.remove();
            this.interstitialLoadListener = null;
        }
        if (this.interstitialFailListener) {
            this.interstitialFailListener.remove();
            this.interstitialFailListener = null;
        }

        return new Promise((resolve) => {
            // Listen for load success
            this.interstitialLoadListener = AdMob.addListener(
                InterstitialAdPluginEvents.Loaded,
                () => {
                    debugLog('Interstitial ad loaded and ready');
                    this.interstitialReady = true;
                    resolve(true);
                }
            );

            // Listen for load failure
            this.interstitialFailListener = AdMob.addListener(
                InterstitialAdPluginEvents.FailedToLoad,
                (error) => {
                    console.error('Interstitial ad failed to load:', error);
                    this.interstitialReady = false;
                    resolve(false);
                }
            );

            // Start loading the ad
            const adId = this.getAdUnitId('interstitial');

            // Let AdMob 8.0.0 SDK handle immersive mode automatically
            // The updated SDK (Google Play Services Ads 24.9+) should handle Android 15 edge-to-edge correctly
            AdMob.prepareInterstitial({
                adId,
                isTesting: isDebugMode(),
                immersiveMode: true,
            })
                .then(() => {
                    debugLog('Interstitial ad preparation started');
                })
                .catch((error) => {
                    console.error('Failed to prepare interstitial ad:', error);
                    this.interstitialReady = false;
                    resolve(false);
                });
        });
    }

    /**
     * Show an interstitial ad
     * @param {number} timeout - Max time in ms to wait for the ad to show (default: 5000)
     * @returns {Promise<boolean>} - Resolves to true if ad was shown, false otherwise
     */
    async showInterstitial(timeout = 5000) {
        // Skip if ads are disabled
        if (this.adsDisabled) {
            debugLog('Ads disabled via REACT_APP_DISABLE_ADS');
            return false;
        }

        // For web platform, trigger web interstitial callback
        if (this.platform === 'web') {
            if (!this.interstitialReady) {
                debugLog('Web interstitial not ready');
                return false;
            }

            if (this.webInterstitialCallbacks.onShow) {
                debugLog('Showing web interstitial (AdSense)');
                this.webInterstitialCallbacks.onShow();
                return true;
            } else {
                debugLog('Web interstitial callback not registered');
                return false;
            }
        }

        // Check if ad is ready
        if (!this.interstitialReady) {
            debugLog('Interstitial ad not ready, skipping');
            return false;
        }

        return new Promise((resolve) => {
            let resolved = false;

            // Set up timeout
            const timeoutId = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    debugLog('Interstitial ad show timed out');
                    resolve(false);
                }
            }, timeout);

            // Listen for showed event
            const showedListener = AdMob.addListener(
                InterstitialAdPluginEvents.Showed,
                () => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        debugLog('Interstitial ad shown successfully');
                        this.interstitialReady = false; // Reset ready state after showing
                        showedListener.remove();
                        resolve(true);
                    }
                }
            );

            // Listen for failed to show
            const failedListener = AdMob.addListener(
                InterstitialAdPluginEvents.FailedToLoad,
                () => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        debugLog('Interstitial ad failed to show');
                        this.interstitialReady = false;
                        failedListener.remove();
                        resolve(false);
                    }
                }
            );

            // Try to show the ad
            AdMob.showInterstitial()
                .then(() => {
                    debugLog('Interstitial ad show requested');
                })
                .catch((error) => {
                    if (!resolved) {
                        resolved = true;
                        clearTimeout(timeoutId);
                        console.error('Failed to show interstitial ad:', error);
                        this.interstitialReady = false;
                        showedListener.remove();
                        failedListener.remove();
                        resolve(false);
                    }
                });
        });
    }

    /**
     * Prepare a rewarded ad
     */
    async prepareRewarded() {
        if (!this.initialized) {
            await this.initialize();
        }

        if (this.platform === 'web') {
            debugLog('Rewarded ads not available on web');
            return;
        }

        try {
            const adId = this.getAdUnitId('rewarded');

            await AdMob.prepareRewardVideoAd({
                adId,
            });

            debugLog('Rewarded ad prepared');
        } catch (error) {
            console.error('Failed to prepare rewarded ad:', error);
        }
    }

    /**
     * Show a rewarded ad and return a promise that resolves when the user
     * has earned the reward
     */
    async showRewarded() {
        if (this.platform === 'web') {
            return { rewarded: false, reason: 'web_not_supported' };
        }

        return new Promise((resolve) => {
            // Listen for reward event
            const rewardListener = AdMob.addListener(
                RewardAdPluginEvents.Rewarded,
                (reward) => {
                    debugLog('User earned reward:', reward);
                    rewardListener.remove();
                    resolve({ rewarded: true, reward });
                }
            );

            // Listen for dismissal without reward
            const dismissListener = AdMob.addListener(
                RewardAdPluginEvents.Dismissed,
                () => {
                    debugLog('Rewarded ad dismissed');
                    dismissListener.remove();
                }
            );

            // Show the ad
            AdMob.showRewardVideoAd()
                .then(() => {
                    debugLog('Rewarded ad shown');
                })
                .catch((error) => {
                    console.error('Failed to show rewarded ad:', error);
                    rewardListener.remove();
                    dismissListener.remove();
                    resolve({ rewarded: false, reason: 'show_failed' });
                });
        });
    }

    /**
     * Add event listeners for ad events
     */
    addBannerListeners(callbacks = {}) {
        if (this.platform === 'web') return { remove: () => { } };

        const listeners = [];

        if (callbacks.onLoaded) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.Loaded, callbacks.onLoaded)
            );
        }
        if (callbacks.onFailed) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.FailedToLoad, callbacks.onFailed)
            );
        }
        if (callbacks.onOpened) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.Opened, callbacks.onOpened)
            );
        }
        if (callbacks.onClosed) {
            listeners.push(
                AdMob.addListener(BannerAdPluginEvents.Closed, callbacks.onClosed)
            );
        }

        return {
            remove: () => listeners.forEach((l) => l.remove()),
        };
    }

    addInterstitialListeners(callbacks = {}) {
        // For web platform, store callbacks for later use
        if (this.platform === 'web') {
            if (callbacks.onDismissed) {
                this.webInterstitialCallbacks.onDismiss = callbacks.onDismissed;
            }
            return {
                remove: () => {
                    this.webInterstitialCallbacks.onDismiss = null;
                }
            };
        }

        const listeners = [];

        if (callbacks.onLoaded) {
            listeners.push(
                AdMob.addListener(InterstitialAdPluginEvents.Loaded, callbacks.onLoaded)
            );
        }
        if (callbacks.onFailed) {
            listeners.push(
                AdMob.addListener(InterstitialAdPluginEvents.FailedToLoad, callbacks.onFailed)
            );
        }
        if (callbacks.onShowed) {
            listeners.push(
                AdMob.addListener(InterstitialAdPluginEvents.Showed, callbacks.onShowed)
            );
        }
        if (callbacks.onDismissed) {
            listeners.push(
                AdMob.addListener(InterstitialAdPluginEvents.Dismissed, callbacks.onDismissed)
            );
        }

        return {
            remove: () => listeners.forEach((l) => l.remove()),
        };
    }

    /**
     * Register callback for showing web interstitial
     * This is called by the WebInterstitialAd component
     */
    registerWebInterstitialCallback(onShow) {
        this.webInterstitialCallbacks.onShow = onShow;
    }

    /**
     * Trigger web interstitial dismissed callback
     */
    triggerWebInterstitialDismissed() {
        if (this.webInterstitialCallbacks.onDismiss) {
            this.webInterstitialCallbacks.onDismiss();
        }
    }

    /**
     * Get AdSense configuration for web interstitial
     */
    getAdSenseConfig() {
        if (this.platform !== 'web') return null;
        return this.getAdUnitId('interstitial');
    }
}

// Export singleton instance
export const adMobService = new AdMobService();
export default adMobService;
